import { useEffect, useState, useCallback } from "react";
import { Receipt, Truck, Download, CalendarClock } from "lucide-react";
import api from "../../lib/apiClient";
import { toast } from "sonner";
import { ProHeader, StatusBadge, money, fmtDate, Loading, Empty } from "./proUi";
import { downloadInvoice } from "./invoice";

// Commandes reçues en tant que professionnel. Actions réelles: livrer (si PAID), facture.
export default function ProCommandes() {
  const [orders, setOrders] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get("/marketplace/orders")
      .then((r) => setOrders((r.data.orders || []).filter((o) => o.role === "pro")))
      .catch(() => setOrders([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const deliver = async (o) => {
    setBusy(o.id);
    try { await api.post(`/marketplace/orders/${o.id}/deliver`, { note: "" }); toast.success("Commande marquée comme livrée"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
    finally { setBusy(null); }
  };

  if (orders === null) return <Loading />;
  return (
    <div className="space-y-6" data-testid="pro-commandes-page">
      <ProHeader eyebrow="Mon activité" title="Mes commandes"
        subtitle="Suis tes commandes, livre tes prestations et récupère tes factures." />
      {orders.length === 0 ? (
        <Empty icon={Receipt} title="Aucune commande pour l'instant" hint="Tes commandes apparaîtront ici dès qu'un client aura accepté et payé un devis." testid="pro-commandes-empty" />
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.id} className="bg-[#18181E] border border-zinc-800 rounded-2xl p-5" data-testid={`commande-${o.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-display font-bold text-zinc-100">{o.title || o.request_title || "Prestation"}</h3>
                  <p className="text-xs text-zinc-500 mt-0.5">Client : {o.client_name} · {fmtDate(o.created_at)}</p>
                </div>
                <StatusBadge status={o.status} />
              </div>
              {o.booking?.start && (
                <p className="text-xs text-violet-300 mt-2 inline-flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5" />Créneau réservé : {fmtDate(o.booking.start)}</p>
              )}
              <div className="flex items-center gap-4 mt-3">
                <span className="font-display text-xl font-extrabold text-amber-400">{money(o.amount, o.currency)}</span>
                <span className="text-xs text-zinc-500">Tu perçois {money(o.pro_amount, o.currency)}</span>
              </div>
              <div className="flex flex-wrap gap-3 mt-4 pt-3 border-t border-zinc-800/70">
                {o.status === "PAID" && (
                  <button onClick={() => deliver(o)} disabled={busy === o.id} data-testid={`commande-deliver-${o.id}`} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl gold-gradient text-black font-semibold text-sm disabled:opacity-60"><Truck className="w-4 h-4" />Marquer comme livrée</button>
                )}
                {!["PENDING_PAYMENT", "CANCELLED"].includes(o.status) && (
                  <button onClick={() => downloadInvoice(o.id, o.invoice_number)} data-testid={`commande-invoice-${o.id}`} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl border border-zinc-700 text-zinc-200 text-sm hover:border-amber-500/50"><Download className="w-4 h-4" />Facture</button>
                )}
                {o.status === "DELIVERED" && <span className="text-xs text-zinc-500 self-center">En attente de validation du client.</span>}
                {o.status === "COMPLETED" && <span className="text-xs text-emerald-400 self-center">Prestation finalisée et payée ✅</span>}
                {o.status === "DISPUTED" && <span className="text-xs text-red-400 self-center">Litige en cours — l'équipe Cehnura examine la commande.</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
